/**
 * One collection (or "All sounds") as a Spotify-style playlist page: a header — large artwork (mosaic / the All sounds
 * gradient), "COLLECTION · <brand>", the big name (click to rename a real collection), "N sounds · 4 min 30 s · updated
 * 2d ago" — then an action row (the neon Play, Shuffle, ⋯ Rename / Change brand… / Delete collection, "← All
 * collections") and the TrackTable. All sounds adds a slim toolbar (brand pills, search, Everyone / Only me, source).
 * Play queues the table in order (Shuffle: a random order) and the bottom player moves on by itself.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { ArrowLeft, Loader2, MoreHorizontal, Pencil, Search, Shuffle, Tag, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/components/ui/utils';
import type { StudioTrack, TrackSource } from '@/api/audioStudioApi';
import { usePlayer } from './AudioPlayer';
import { BrandAvatar, BrandPickerDialog, BrandPills, useStudioBrands, type StudioBrand } from './brands';
import { AllSoundsArtwork, CollectionArtwork, NeonPlayButton } from './CollectionArt';
import { ScopeSelect, type SoundsScope } from './SoundRows';
import { TrackTable } from './TrackTable';
import { OriginalsNote } from './TrendBits';
import { ALL_SOUNDS, EMPTY_COLLECTION_TEXT, allSoundsParams, allSoundsQueueKey, collectionQueueKey } from './collectionKeys';
import { COLLECTION_NAME_MAX, collectionErrorMessage, useDeleteCollection, useUpdateCollection } from './collectionMutations';
import { formatAgo, formatTotalDuration, soundCount, studioPlayable, useNow } from './format';
import { useCollection, useStudioTracks } from './queries';
import { useTrackActions } from './useTrackActions';

const SOURCES: Array<{ id: '' | TrackSource; label: string }> = [
  { id: '', label: 'All sources' },
  { id: 'prompt', label: 'Prompt' },
  { id: 'preset', label: 'Preset' },
  { id: 'trend_lookalike', label: 'Trend lookalike' },
];

export function CollectionPage({ id, brandFilter, onBrandFilter, allScope, onBack }: {
  /** A collection id, or `ALL_SOUNDS`. */
  id: string;
  brandFilter: string | null;
  onBrandFilter: (id: string | null) => void;
  allScope: SoundsScope;
  /** "← All collections" (also after a delete, or when the collection cannot be opened). */
  onBack: () => void;
}) {
  return id === ALL_SOUNDS
    ? <AllSoundsPage brandFilter={brandFilter} onBrandFilter={onBrandFilter} initialScope={allScope} onBack={onBack} />
    : <OneCollectionPage id={id} onBack={onBack} />;
}

const totalLength = (tracks: StudioTrack[]) => formatTotalDuration(tracks.reduce((s, t) => s + (t.duration_s || 0), 0));

// ─── Shared layout ──────────────────────────────────────────────────────────

function BackLink({ onBack }: { onBack: () => void }) {
  return (
    <button type="button" onClick={onBack} className="inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-[13px] text-[#667085] hover:bg-[#f2f4f7] hover:text-[#101828]" style={{ fontWeight: 600 }}>
      <ArrowLeft className="h-4 w-4" aria-hidden /> All collections
    </button>
  );
}

function PageHeader({ artwork, eyebrow, title, meta }: { artwork: ReactNode; eyebrow: ReactNode; title: ReactNode; meta: string }) {
  return (
    <div className="flex flex-col items-center gap-5 text-center sm:flex-row sm:items-end sm:gap-6 sm:text-left">
      <div className="h-[140px] w-[140px] shrink-0 overflow-hidden rounded-[12px] shadow-sm sm:h-44 sm:w-44">{artwork}</div>
      <div className="min-w-0 max-w-full flex-1 pb-1">
        <div className="flex items-center justify-center gap-1.5 text-[11.5px] uppercase tracking-wide text-[#667085] sm:justify-start" style={{ fontWeight: 600 }}>{eyebrow}</div>
        <div className="mt-1.5">{title}</div>
        <p className="mt-2 text-[13px] text-[#667085]">{meta}</p>
      </div>
    </div>
  );
}

/** The neon Play + Shuffle for a list. Play pauses / resumes the list when it is the one in the player. */
function PlayControls({ tracks, queueKey, name }: { tracks: StudioTrack[]; queueKey: string; name: string }) {
  const player = usePlayer();
  const [shuffle, setShuffle] = useState(false);
  const mine = player.queueKey === queueKey;
  const playing = mine && player.isPlaying;
  const playable = tracks.some((t) => t.audio_url);
  const play = () => {
    if (mine) player.pauseResume();
    else player.playQueue(tracks.map(studioPlayable), shuffle ? undefined : 0, { shuffle, key: queueKey });
  };
  return (
    <>
      <NeonPlayButton size="lg" playing={playing} label={playing ? `Pause ${name}` : `Play ${name}`} onClick={play} disabled={!playable} />
      <button
        type="button"
        aria-pressed={shuffle}
        aria-label="Shuffle"
        title={shuffle ? 'Shuffle on' : 'Shuffle off'}
        onClick={() => setShuffle((s) => !s)}
        className={cn('relative flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-[#f2f4f7]', shuffle ? 'text-[#be43ff]' : 'text-[#667085] hover:text-[#101828]')}
      >
        <Shuffle className="h-5 w-5" aria-hidden />
        {shuffle && <span className="absolute bottom-1 h-1 w-1 rounded-full bg-[#be43ff]" aria-hidden />}
      </button>
    </>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return <div className="rounded-[16px] bg-[#f9fafb] px-6 py-12 text-center text-[13px] text-[#667085]">{children}</div>;
}

function LoadingBlock({ label }: { label: string }) {
  return <div className="flex items-center justify-center gap-2 py-16 text-[13px] text-[#667085]" role="status"><Loader2 className="h-4 w-4 animate-spin text-[#98a2b3]" aria-hidden /> {label}</div>;
}

// ─── All sounds ─────────────────────────────────────────────────────────────

/**
 * All sounds as a full page (header + controls), or `embedded` under the collections on the Your Collections home:
 * a section title with the count and Play / Shuffle, then the same toolbar and table (brand pills live above, on the home).
 */
export function AllSoundsPage({ brandFilter, onBrandFilter, initialScope, onBack, embedded = false }: { brandFilter: string | null; onBrandFilter: (id: string | null) => void; initialScope: SoundsScope; onBack?: () => void; embedded?: boolean }) {
  const [scope, setScope] = useState<SoundsScope>(initialScope);
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  const [source, setSource] = useState<'' | TrackSource>('');
  useEffect(() => { const t = setTimeout(() => setQ(text.trim()), 300); return () => clearTimeout(t); }, [text]);
  const brand = useStudioBrands().brandOf(brandFilter);
  const name = brand ? `All ${brand.brandName} sounds` : 'All sounds';

  const tracks = useStudioTracks({ ...allSoundsParams(brandFilter, scope), q: q || undefined, source: source || undefined });
  const actions = useTrackActions();
  const now = useNow();
  const list = tracks.data?.tracks ?? [];
  const meta = [tracks.data ? soundCount(tracks.data.total) : null, totalLength(list)].filter(Boolean).join(' · ');

  return (
    <section aria-label={name} className={embedded ? 'space-y-4' : 'space-y-6'}>
      {embedded ? (
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0">
            <h2 className="text-[20px] font-bold leading-7 tracking-[-0.01em] text-[#101828]">{name}</h2>
            {meta && <p className="text-[12.5px] leading-[18px] text-[#667085]">{meta}</p>}
          </div>
          <div className="ml-auto flex items-center gap-2"><PlayControls tracks={list} queueKey={allSoundsQueueKey} name={name} /></div>
        </div>
      ) : (
        <>
          <PageHeader
            artwork={<AllSoundsArtwork />}
            eyebrow={brand ? <>Collection · <BrandAvatar brand={brand} size={14} /> {brand.brandName}</> : 'Collection'}
            title={<h2 className="truncate text-[26px] font-bold leading-tight tracking-[-0.02em] text-[#101828] sm:text-[32px]">{name}</h2>}
            meta={meta || ' '}
          />
          <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
            <PlayControls tracks={list} queueKey={allSoundsQueueKey} name={name} />
            {onBack && <span className="sm:ml-auto"><BackLink onBack={onBack} /></span>}
          </div>
        </>
      )}

      <div className="space-y-3">
        {!embedded && <BrandPills value={brandFilter} onChange={onBrandFilter} />}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[180px] flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#98a2b3]" aria-hidden />
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Search your sounds…" aria-label="Search your sounds" className="h-9 w-full rounded-full border border-[#eaecf0] bg-white pl-9 pr-3 text-[13px] focus:border-[#d0d5dd] focus:outline-none focus:ring-2 focus:ring-[#98a2b3]/20" />
          </div>
          <ScopeSelect value={scope} onChange={setScope} />
          <select value={source} onChange={(e) => setSource(e.target.value as '' | TrackSource)} aria-label="Source" className="h-9 rounded-[8px] border border-[#eaecf0] bg-white px-2.5 text-[13px] text-[#344054]">
            {SOURCES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <OriginalsNote className="basis-full sm:ml-auto sm:basis-auto" />
        </div>
      </div>

      {tracks.isLoading ? (
        <LoadingBlock label="Loading sounds…" />
      ) : tracks.isError ? (
        <p className="py-12 text-center text-[13px] text-[#b42318]" role="alert">Could not load sounds: {(tracks.error as Error).message}</p>
      ) : list.length === 0 ? (
        <Empty>{q || source ? 'No sounds match.' : 'Your sounds will appear here: describe one in the Studio or pick a trend.'}</Empty>
      ) : (
        <TrackTable tracks={list} actions={actions} queueKey={allSoundsQueueKey} label={`${name} list`} now={now} showOwner={scope === 'team'} />
      )}
      {actions.dialogs}
    </section>
  );
}

// ─── One collection ─────────────────────────────────────────────────────────

function OneCollectionPage({ id, onBack }: { id: string; onBack: () => void }) {
  const detail = useCollection(id);
  const update = useUpdateCollection();
  const del = useDeleteCollection();
  const actions = useTrackActions({ collectionId: id });
  const { brands, brandOf } = useStudioBrands();
  const now = useNow();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [branding, setBranding] = useState(false);

  if (detail.isLoading) return <LoadingBlock label="Loading collection…" />;
  if (detail.isError || !detail.data) {
    return (
      <div className="py-12 text-center text-[13px]" role="alert">
        <p className="text-[#b42318]">Could not open this collection{detail.error ? `: ${(detail.error as Error).message}` : ''}.</p>
        <button type="button" onClick={onBack} className="mt-2 text-[#6941c6] underline-offset-2 hover:underline">Back to all collections</button>
      </div>
    );
  }

  const c = detail.data;
  const brand: StudioBrand | undefined = brandOf(c.brand_id);
  const startRename = () => { setName(c.name); setError(null); setRenaming(true); };
  const saveRename = () => {
    const n = name.trim();
    if (!n || n === c.name) { setRenaming(false); return; }
    update.mutate({ id, name: n }, {
      onSuccess: () => { toast.success('Collection renamed'); setRenaming(false); },
      onError: (e) => setError(collectionErrorMessage(e)),
    });
  };
  const changeBrand = (brandId: string) => {
    if (brandId === (c.brand_id || '')) { setBranding(false); return; }
    update.mutate({ id, brand_id: brandId }, {
      onSuccess: () => { toast.success(brandId ? `Brand set to ${brandOf(brandId)?.brandName ?? 'the brand'}` : 'Brand removed'); setBranding(false); },
      onError: (e) => toast.error(collectionErrorMessage(e)),
    });
  };
  const meta = [soundCount(c.track_count), totalLength(c.tracks), c.updated_at ? `updated ${formatAgo(c.updated_at, now)}` : null].filter(Boolean).join(' · ');

  const title = renaming ? (
    <form className="mx-auto max-w-md space-y-1 sm:mx-0" onSubmit={(e) => { e.preventDefault(); saveRename(); }}>
      <div className="flex items-center gap-1.5">
        <input
          autoFocus
          value={name}
          onChange={(e) => { setName(e.target.value); setError(null); }}
          onKeyDown={(e) => { if (e.key === 'Escape') setRenaming(false); }}
          maxLength={COLLECTION_NAME_MAX}
          aria-label="Collection name"
          aria-invalid={Boolean(error)}
          className="h-11 min-w-0 flex-1 rounded-[10px] border border-[#d0d5dd] bg-white px-3 text-[18px] font-semibold focus:outline-none focus:ring-2 focus:ring-[#be43ff]/20"
        />
        <Button type="submit" size="sm" disabled={update.isPending} className="h-11 rounded-[10px] bg-[#2e024c] px-4 text-[13px] text-white hover:bg-[#45066f]">
          {update.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />} Save
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => setRenaming(false)} className="h-11 rounded-[10px] px-3 text-[13px] text-[#667085]">Cancel</Button>
      </div>
      {error && <p className="text-left text-[12px] text-[#b42318]" role="alert">{error}</p>}
    </form>
  ) : (
    <h2 className="truncate text-[26px] font-bold leading-tight tracking-[-0.02em] text-[#101828] sm:text-[32px]">
      <button type="button" onClick={startRename} title="Rename" className="max-w-full truncate rounded-md text-left outline-none hover:opacity-80 focus-visible:ring-2 focus-visible:ring-[#be43ff]/30">{c.name}</button>
    </h2>
  );

  return (
    <section aria-label={c.name} className="space-y-6">
      <PageHeader
        artwork={<CollectionArtwork name={c.name} covers={c.cover_urls} />}
        eyebrow={brand ? <>Collection · <BrandAvatar brand={brand} size={14} /> <span className="normal-case">{brand.brandName}</span></> : 'Collection'}
        title={title}
        meta={meta}
      />
      <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
        <PlayControls tracks={c.tracks} queueKey={collectionQueueKey(id)} name={c.name} />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" aria-label="Collection actions" className="flex h-10 w-10 items-center justify-center rounded-full text-[#667085] hover:bg-[#f2f4f7] hover:text-[#101828] data-[state=open]:bg-[#f2f4f7]"><MoreHorizontal className="h-5 w-5" aria-hidden /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[190px]">
            <DropdownMenuItem onSelect={startRename}><Pencil className="h-4 w-4" aria-hidden /> Rename</DropdownMenuItem>
            {brands.length > 0 && <DropdownMenuItem onSelect={() => setBranding(true)}><Tag className="h-4 w-4" aria-hidden /> Change brand…</DropdownMenuItem>}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => setConfirming(true)} className="text-[#b42318] focus:text-[#b42318]"><Trash2 className="h-4 w-4" aria-hidden /> Delete collection</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <span className="sm:ml-auto"><BackLink onBack={onBack} /></span>
      </div>
      <OriginalsNote />

      {c.tracks.length === 0 ? (
        <Empty>
          <p className="text-[14px] text-[#101828]" style={{ fontWeight: 600 }}>This collection is empty</p>
          <p className="mt-1">{EMPTY_COLLECTION_TEXT}</p>
        </Empty>
      ) : (
        <TrackTable tracks={c.tracks} actions={actions} queueKey={collectionQueueKey(id)} label={`${c.name} sounds`} now={now} showOwner />
      )}
      {actions.dialogs}

      <BrandPickerDialog open={branding} onOpenChange={setBranding} title="Change brand" subject={`“${c.name}”`} current={c.brand_id} busy={update.isPending} onPick={changeBrand} />

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{c.name}”?</AlertDialogTitle>
            <AlertDialogDescription>The collection is removed for your team. Its sounds are kept.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                del.mutate(id, {
                  onSuccess: () => { toast.success('Collection deleted'); setConfirming(false); onBack(); },
                  onError: (err) => toast.error(collectionErrorMessage(err)),
                });
              }}
              className="bg-[#d92d20] text-white hover:bg-[#b42318]"
            >
              {del.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />} Delete collection
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
